import { TradeInCsl } from "../../TradeInCsl";
import { TradeInAemNavStep, TradeInAemSearchType } from "../../../utils/constants";
import type { TradeInAemStateType } from "../../../redux/store";
import { type TradeInAemLabelType, getTradeInAemLabel } from "../../../utils/labels";
import type {
    TradeInAemEHubPayload,
    TradeInAemExtraVehicleDetailsPdfBaseType,
    TradeInAemInfoBlockContent,
    TradeInAemFetchQuoteFunctionReturnType,
    TradeInAemVehicleExtraInfoType,
    TradeInAemVehicleNameType,
    TradeInAemVehicleType,
    TradeInAemVehicleValueType,
    TradeInAemFetchVehicleResultsReturnType,
} from "../../../utils/types";
import {
    enablePdf,
    fetchFindVehicle,
    fetchVehicleValue,
    getParsedVehicleDetails,
    getTradeInAemPdfUrl,
    handleQuoteFetch,
    handleVehicleFetch,
} from "../../../utils/utils";
import type { TradeInAemExternalDataType } from "../../../../common-deprecated/features/tradein/redux/reducers/TradeInAemExternalDataReducer";
import type { DealerFinderSettingsType } from "../../../../common-deprecated/components/AemDealerFinder";

// The TGB BCA api documentation can be found here: https://cde.toyota-europe.com/confluence/display/IT/BCA
export type TradeInAemVehicleTypeGb = {
    plate: string;
    manufacturer: string;
    model: string;
    engine: string;
    grade: string;
    body: string;
    doors: string;
    fuel: string;
    transmission: string;
    registrationDate: string;
    type: string;
    description: string;
    vin: string;
    colour: string;
    correlationId: string;
    name: string;

    // Extra info property - used to save some data which will be used for the PDF later
    tmeExtraInfo?: TradeInAemVehicleExtraInfoType;
};

export type TradeInAemGbLicenseApiResponseType = TradeInAemVehicleTypeGb & {
    alternatives?: Omit<TradeInAemVehicleTypeGb, "correlationId">[];
};

export type TradeInAemGbValueApiResponseType = TradeInAemGbLicenseApiResponseType & {
    [index: string]: unknown; // This object has a lot of additional properties, only typing the ones used for now.
    quote: {
        [index: string]: unknown;
        id: string;
        correlationId: string;
        value: {
            min: number;
            expires: string; // ISO timestamp.
            rules: string[];
            detail: [
                {
                    value: { min: number; max: number };
                    rule: string;
                    operation?: string;
                    totalWithout?: { min: number };
                },
            ];
        };
    };
};

// Specific properties which are only shown for TGB in the vehicle extra details in the PDF
export type TradeInAemExtraVehicleDetailsPdfGbSpecificType = {
    type: string;
};

// Combination of base props + TGB specific props
type TradeInAemGbExtraVehicleDetailsPdfType = TradeInAemExtraVehicleDetailsPdfBaseType &
    TradeInAemExtraVehicleDetailsPdfGbSpecificType;

export class TradeInCslGb extends TradeInCsl {
    // ----------------------------------------------------------------------
    // Static properties
    // ----------------------------------------------------------------------
    enabledNavSteps = [TradeInAemNavStep.FindVehicle, TradeInAemNavStep.VehicleDetails, TradeInAemNavStep.VehicleValue];

    enableVin = false;

    enableLicensePlate = true;

    enableResolve = false;

    enablePreviouslySearchedVehicles = false;

    resolveKeyMap: Record<string, TradeInAemLabelType> = {};

    dealerFinderSettings: DealerFinderSettingsType = {};

    testPlates = [{ value: "YC60FNK", description: "" }];

    // ----------------------------------------------------------------------
    // Selectors
    // ----------------------------------------------------------------------
    vehicleValueSelector(state: TradeInAemStateType): TradeInAemVehicleValueType | null {
        const valuation = state.tradeInAemVehicleValue.valuation as TradeInAemGbValueApiResponseType | null;

        if (!valuation) return null;

        const vehicleValue: TradeInAemVehicleValueType = {
            value: valuation.quote.value.min,
            validityDate: valuation.quote.value.expires,
            quoteId: valuation.quote.id,
        };

        // Outstanding loan.
        // Read the necessary values from the api response (this depends on config in the trade-in API).
        const loanDetail = valuation?.quote.value.detail.find((item) => item.rule.includes("Outstanding loan"));
        if (loanDetail && loanDetail.value.min && loanDetail.totalWithout?.min) {
            vehicleValue.outstandingLoan = {
                originalEstimation: loanDetail.totalWithout?.min,
                outstandingLoan: Math.abs(loanDetail.value.min),
            };
        }

        return vehicleValue;
    }

    vehiclePlateOrVinSelector(state: TradeInAemStateType): string {
        const { searchType } = state.tradeInAemFindVehicle;
        const vehicle = state.tradeInAemVehicle.vehicle as TradeInAemVehicleTypeGb;
        return searchType === TradeInAemSearchType.LicensePlate ? vehicle.plate : vehicle.vin;
    }

    vehicleInfoBlockSelector(state: TradeInAemStateType): TradeInAemInfoBlockContent {
        const vehicle = state.tradeInAemVehicle.vehicle as TradeInAemVehicleTypeGb;
        if (!vehicle) return [];

        const result: { label: string; value: string }[] = [
            { label: getTradeInAemLabel(state, "tradeInAemVehicleDetailsLicensePlate"), value: vehicle.plate },
        ].concat(getParsedVehicleDetails(state.tradeInAemVehicleDetails.formItems));

        return result;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    vehicleQuoteExpirySelector(state: TradeInAemStateType): string {
        // Expiry should not be shown anywhere, see OR-7390
        return "";
        // const valuation = state.tradeInAemVehicleValue.valuation as TradeInAemGbValueApiResponseType | null;
        // if (!valuation) return "";
        //
        // const date = new Intl.DateTimeFormat(state.commonSettings.culture.name, {
        //     day: "numeric",
        //     month: "long",
        //     year: "numeric",
        // }).format(new Date(valuation.quote.value.expires));
        //
        // return getTradeInAemLabel(state, "tradeInAemVehicleValueValidity").replace("{date}", date);
    }

    // ----------------------------------------------------------------------
    // Non-selector functions
    // ----------------------------------------------------------------------
    getExtDataFromValuation = (valuation: TradeInAemGbValueApiResponseType): TradeInAemExternalDataType | null => {
        const { quote } = valuation;

        return {
            quoteId: quote.id,
            vehicleName: this.getVehicleName(valuation),
            vehicleValue: quote.value.min,
            licensePlate: valuation.plate,
            vin: valuation.vin,
        };
    };

    getVehicleName(vehicle: TradeInAemVehicleType): TradeInAemVehicleNameType {
        const typedVehicle = vehicle as TradeInAemVehicleTypeGb;
        return {
            name: `${typedVehicle.manufacturer} ${typedVehicle.model}`,
            description: typedVehicle.description,
        };
    }

    getVehicleKey(vehicle: TradeInAemVehicleType): string {
        const typedVehicle = vehicle as TradeInAemVehicleTypeGb;
        return `${typedVehicle.manufacturer}-${typedVehicle.model}-${typedVehicle.grade}-${typedVehicle.vin}`;
    }

    async fetchVehicleResults(value: string, type: TradeInAemSearchType): Promise<void> {
        const { dispatch, getState } = this.getStore();
        const { commonSettings, tradeInAemSettings } = getState();

        const fetchFn = async (): Promise<TradeInAemFetchVehicleResultsReturnType> => {
            const headers = this.getTradeInApiHeaders("GET");
            const response = await fetchFindVehicle(value, type, commonSettings, tradeInAemSettings, headers);
            if (response.status === 200) {
                let searchResults: TradeInAemVehicleType[];
                const jsonResponse = (await response.json()) as unknown as TradeInAemGbLicenseApiResponseType;
                const { alternatives, ...vehicle } = jsonResponse;

                if (alternatives?.length) {
                    // Attach correlationId to the alternatives as this is required by the value API.
                    const parsedAlternatives = alternatives.map((alternative) => ({
                        ...alternative,
                        correlationId: (vehicle as TradeInAemVehicleTypeGb).correlationId,
                    }));
                    searchResults = [vehicle, ...parsedAlternatives];
                } else {
                    searchResults = [vehicle as TradeInAemVehicleTypeGb];
                }
                return { results: searchResults };
            } else {
                return { results: [] };
            }
        };

        await handleVehicleFetch(dispatch, fetchFn);
    }

    async fetchVehicleQuote(): Promise<void> {
        const { dispatch, getState } = this.getStore();
        const { tradeInAemVehicle, commonSettings, tradeInAemSettings } = getState();
        const { vehicle } = tradeInAemVehicle;

        const fetchFn = async (): TradeInAemFetchQuoteFunctionReturnType => {
            const headers = this.getTradeInApiHeaders("POST");
            const response = await fetchVehicleValue(commonSettings, tradeInAemSettings, vehicle, headers);
            const jsonResponse = await response.json();
            if (response.status === 200) {
                const parsedResponse = jsonResponse as unknown as TradeInAemGbValueApiResponseType;

                // TGB API can return a negative valuation, handle this as a failure.
                if (parsedResponse.quote.value.min < 0) return { valuation: null, latestResponse: jsonResponse };

                return { valuation: parsedResponse, latestResponse: parsedResponse };
            }
            return { valuation: null, latestResponse: jsonResponse };
        };

        await handleQuoteFetch(dispatch, fetchFn);
    }

    getEHubVehicleData(): TradeInAemEHubPayload["vehicle"] {
        const currentState = this.getStore().getState();
        const vehicleName = this.vehicleNameSelector(currentState);
        const { tradeInAemVehicle, tradeInAemVehicleDetails, commonSettings } = currentState;
        const { tradeInAemSettings } = currentState;
        const vehicle = tradeInAemVehicle.vehicle as TradeInAemVehicleTypeGb;

        const quoteId = this.vehicleValueSelector(currentState)?.quoteId || null;
        const pdfLink =
            enablePdf(commonSettings, tradeInAemSettings) && quoteId
                ? getTradeInAemPdfUrl(commonSettings, quoteId)
                : null;

        return {
            quoteId,
            licensePlate: vehicle.plate,
            vin: vehicle.vin,
            name: `${vehicleName.name} - ${vehicleName.description || ""}`,
            manufacturer: vehicle.manufacturer,
            model: vehicle.model,
            bodyType: vehicle.body,
            mileage: (tradeInAemVehicleDetails.formItems?.mileage?.value as string) || null,
            engine: vehicle.engine,
            transmission: vehicle.transmission,
            doors: vehicle.doors,
            fuelType: vehicle.fuel,
            co2: null,
            modelYear: null,
            pdfLink,
            registrationDate: null,
        };
    }

    getExtraVehicleDetailsPdf = (): TradeInAemGbExtraVehicleDetailsPdfType => {
        const currentState = this.getStore().getState();
        const { tradeInAemVehicle } = currentState;
        // The vehicle on the PDF is fetched via the quoteId, which is propagated from the value api.
        const vehicle = tradeInAemVehicle.vehicle as TradeInAemGbValueApiResponseType;

        const baseObject: TradeInAemExtraVehicleDetailsPdfBaseType = {
            manufacturer: vehicle.manufacturer,
            model: vehicle.model,
            grade: vehicle.grade,
            generation: null,
            engine: vehicle.engine,
            powerHp: null,
            powerPs: null,
            powerKw: null,
            body: vehicle.body,
            doors: vehicle.doors,
            fuel: vehicle.fuel,
            transmission: vehicle.transmission,
            gears: null,
            colour: vehicle.colour,
            regDate: vehicle.registrationDate?.split("T")?.[0] || null,
            vin: vehicle.vin,
            seats: null,
            wheeldrive: null,
            cc: null,
            year: null,
        };

        // TGB specific
        const csObject: TradeInAemExtraVehicleDetailsPdfGbSpecificType = {
            type: vehicle.type,
        };

        return { ...baseObject, ...csObject };
    };

    getTradeInApiHeaders(method: "GET" | "POST"): HeadersInit | undefined {
        // This header was added during the migration from BCA v1 to v2
        // BCA v2 is now the default at our side, so we technically don't need the header anymore.
        // Volta confirmed the header is still required.
        // Volta reference: "We currently support both BCA v1 and BCA v2, and the X-TME-VERSION header allows for compatibility between the two versions. At this moment, there are no immediate plans to remove support for X-TME-VERSION. However, if the decision is made to fully transition to only BCA v2, we can proceed with its removal."
        return { ...super.getTradeInApiHeaders(method), "X-TME-VERSION": "2" };
    }

    prefillFormsWithTestData(): void {
        super.prefillFormsWithTestData();

        // Prefill custom vehicle details values
        this.setFormItemValue("mileage", "100000");
        this.setFormItemValue("motStatus", "Yes");
        this.setFormItemValue("ownership", "Yes");
        this.setFormItemValue("insuranceLoss", "No");
        this.setFormItemValue("taxiPrivateHire", "No");
        this.setFormItemValue("emergencyServices", "No");
        this.setFormItemValue("ukFirstRegistered", "Yes");
        this.setFormItemValue("beenModified", "No");
        this.setFormItemValue("mechanicalCondition", "Yes");
        this.setFormItemValue("cosmeticCondition", "Excellent");
        this.setFormItemValue("totalKeepers", "3");
        this.setFormItemValue("ServiceHistory", "Full");
        this.setFormItemValue("keys", "2");
    }
}
